import { SerialNumberParser } from '@calibrite-org/xrite-serial-number-parser';
import { safeGet } from '../utils/safeStorage';

// API base URL - determine from environment
const API_BASE_URL = typeof window !== 'undefined' && window.location?.href?.includes("localhost")
  ? "http://localhost:3001"
  : "https://account.calibrite.com";

// Initialize serial number parser
const serialParser = new SerialNumberParser();

// In-memory cache
let userDataCache = null;
let cacheExpiry = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Parse user data from API response
 * @param {Object} data - Raw user data from API
 * @returns {Object} Parsed user data
 */
export const parseUserData = (data) => {
  const { user, collection, strategy, exp, token } = data;
  
  return {
    user,
    collection,
    strategy,
    exp,
    token
  };
};

/**
 * Parse device data from user object
 * @param {Object} userData - User data containing device associations
 * @returns {Array} Array of parsed device objects
 */
export const parseDevices = (userData) => {
  const devices = [];

  // Add registered devices
  if (userData?.user?.deviceAssociations && Array.isArray(userData.user.deviceAssociations)) {
    devices.push(...userData.user.deviceAssociations.map(association => {
      const serial = association.device?.serialNumber;
      let productType = 'Display';
      let productModel = 'Unknown';

      try {
        if (serial) {
          const serialDetails = serialParser.parseSerialNumber(serial);
          // productType = serialDetails.name.split(' ')[0];
          productModel = serialDetails.name;
        }
      } catch (err) {
        console.warn('Failed to parse serial number:', err);
      }

      const isRegistered = association.optedIn || false;

      if (!isRegistered) {
        return null;
      }

      return {
        id: association.device?.id,
        serialNumber: serial,
        productType,
        productModel,
        purchaseDate: association.device?.purchaseDate ? new Date(association.device.purchaseDate).toLocaleDateString() : null,
        registrationDate: association.associationDate ? new Date(association.associationDate).toLocaleDateString() : null,
        optedIn: association.optedIn || false,
        isRegistered
      };
    }).filter(device => device !== null));
  }

  // Add unregistered 24 patch target if flag is present
  if (userData?.user?.hasTarget24) {
    devices.push({
      id: 'unregistered-24',
      serialNumber: 'Unknown',
      productType: 'ColorChecker',
      productModel: 'ColorChecker 24 Patch Target',
      purchaseDate: null,
      registrationDate: userData?.user?.hasTarget24Date ? new Date(userData.user.hasTarget24Date).toLocaleDateString() : null,
      optedIn: false,
      isRegistered: false,
      needsRegistration: true
    });
  }

  // Add unregistered 140 patch target if flag is present
  if (userData?.user?.hasTarget140) {
    devices.push({
      id: 'unregistered-140',
      serialNumber: 'Unknown',
      productType: 'ColorChecker',
      productModel: 'ColorChecker 140 Patch Target',
      purchaseDate: null,
      registrationDate: userData?.user?.hasTarget140Date ? new Date(userData.user.hasTarget140Date).toLocaleDateString() : null,
      optedIn: false,
      isRegistered: false,
      needsRegistration: true
    });
  }

  return devices;
};

/**
 * Parse reminder data from user object
 * @param {Object} userData - User data containing reminders
 * @returns {Array} Array of parsed reminder objects
 */
export const parseReminders = (userData) => {
  if (!userData?.user?.reminders || !Array.isArray(userData.user.reminders)) {
    return [];
  }

  const now = new Date();
  
  return userData.user.reminders
    .map(reminder => ({
      id: reminder.id,
      serial: reminder.serial,
      reminderSent: reminder.reminderSent,
      date: reminder.date ? new Date(reminder.date) : null,
      machineHint: reminder.machineHint,
      monitorHint: reminder.monitorHint,
      calibrationType: reminder.calibrationType,
      technologyType: reminder.technologyType,
      softwareVersion: reminder.softwareVersion,
      platform: reminder.platform
    }))
    .filter(reminder => reminder.date && reminder.date > now)
    .map(reminder => ({
      ...reminder,
      date: reminder.date.toLocaleDateString()
    }));
};

/**
 * Get product type based on serial number pattern
 * @param {string} serial - Device serial number
 * @returns {string} Product type
 */
export const getProductTypeFromSerial = (serial) => {
  if (!serial) return 'Unknown';
  
  try {
    const serialDetails = serialParser.parseSerialNumber(serial);
    return serialDetails.name.split(' ')[0]; // Get the brand name (ColorChecker, Calibrite, etc.)
  } catch (err) {
    console.warn('Failed to parse serial number:', err);
    return 'Unknown';
  }
};

/**
 * Get product model based on serial number pattern
 * @param {string} serial - Device serial number
 * @returns {string} Product model
 */
export const getProductModelFromSerial = (serial) => {
  if (!serial) return 'Unknown';
  
  try {
    const serialDetails = serialParser.parseSerialNumber(serial);
    return serialDetails.name;
  } catch (err) {
    console.warn('Failed to parse serial number:', err);
    return 'Unknown';
  }
};

/**
 * Checks if the cache is valid
 * @returns {boolean} Whether the cache is valid
 */
const isCacheValid = () => {
  return userDataCache && cacheExpiry && Date.now() < cacheExpiry;
};

/**
 * Safely get token from localStorage with fallback
 * @returns {string|null} The token or null if not found
 */
const getToken = () => safeGet("payloadToken");

/**
 * Fetch user data from API
 * @param {boolean} forceRefresh - Force a refresh of the cache
 * @returns {Promise<Object>} User data
 */
export const fetchUserData = async (forceRefresh = false) => {
  // Use cached data if available and not forcing refresh
  if (!forceRefresh && isCacheValid()) {
    console.log('Using cached user data');
    return userDataCache;
  }

  try {
    // Check if we're in Storybook or if token is missing
    const token = getToken();
    if (!token) {
      console.error('No token found, unable to fetch user data');
      throw new Error('Authentication token is missing');
    }

    const response = await fetch(`${API_BASE_URL}/api/users/me?depth=1`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch user data');
    }

    const data = await response.json();
    const parsedData = parseUserData(data);
    
    // Update cache
    userDataCache = parsedData;
    cacheExpiry = Date.now() + CACHE_DURATION;
    
    return parsedData;
  } catch (error) {
    console.error('Error fetching user data:', error);
    
    throw error;
  }
};

/**
 * Clear the user data cache
 */
export const clearUserDataCache = () => {
  userDataCache = null;
  cacheExpiry = null;
};

/**
 * Fetch user profile details
 * @returns {Promise<Object>} User profile data
 */
export const fetchUserProfile = async () => {
  const userData = await fetchUserData();
  
  return {
    firstName: userData.user.firstName || '',
    lastName: userData.user.lastName || '',
    email: userData.user.email || '',
    countryCode: userData.user.countryCode || '',
    locale: userData.user.locale || 'en',
    type: userData.user.type || 'user',
    newsletter: userData.user.newsletter || false
  };
};

/**
 * Fetch user devices
 * @returns {Promise<Array>} User devices
 */
export const fetchUserDevices = async () => {
  const userData = await fetchUserData();
  return parseDevices(userData);
};

/**
 * Fetch user reminders
 * @returns {Promise<Array>} User reminders
 */
export const fetchUserReminders = async () => {
  const userData = await fetchUserData();
  return parseReminders(userData);
};

/**
 * Update user profile details
 * @param {Object} profileData - Updated profile data
 * @returns {Promise<Object>} Updated user profile data
 */
export const updateUserProfile = async (profileData) => {
  const token = getToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/users/me`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        countryCode: profileData.countryCode,
        locale: profileData.locale,
        newsletter: profileData.newsletter
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update user profile');
    }

    const data = await response.json();
    
    // Clear cache to ensure next fetch gets updated data
    clearUserDataCache();
    
    return parseUserData(data);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Register a product with Calibrite
 * @param {Object} registrationData - Product registration data
 * @returns {Promise<Object>} Registration response
 */
export const registerProduct = async (registrationData) => {
  const token = getToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  try {
    // TODO: Uncomment this when the API is ready
    // const response = await fetch(`${API_BASE_URL}/api/products/register`, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${token}`
    //   },
    //   body: JSON.stringify({
    //     ...registrationData,
    //     // Parse serial number to validate and get additional details
    //     ...(registrationData.serialNumber ? {
    //       productDetails: serialParser.parseSerialNumber(registrationData.serialNumber)
    //     } : {})
    //   })
    // });

    const response = {
      ok: true,
      json: () => Promise.resolve({
        registrationId: '1234567890',
        message: 'Product registered successfully'
      })
    };

    if (!response.ok) {
      const errorData = await response.json();
      let errorCode = 'UNKNOWN_ERROR';
      
      // Map error codes from API response
      if (errorData.code === 'INVALID_SERIAL_NUMBER') {
        errorCode = 'INVALID_SERIAL';
      } else if (errorData.code === 'PRODUCT_NOT_FOUND') {
        errorCode = 'INVALID_PRODUCT';
      } else if (errorData.code === 'ALREADY_REGISTERED') {
        errorCode = 'DUPLICATE_REGISTRATION';
      } else if (errorData.code === 'VALIDATION_ERROR') {
        errorCode = 'VALIDATION_ERROR';
      }

      const error = new Error(errorData.message || 'Registration failed');
      error.code = errorCode;
      throw error;
    }

    const data = await response.json();
    
    // Clear cache to ensure next fetch gets updated device list
    clearUserDataCache();
    
    return {
      success: true,
      registrationId: data.registrationId,
      message: data.message
    };
  } catch (error) {
    console.error('Error registering product:', error);
    
    if (error.code) {
      throw error;
    }
    
    const networkError = new Error('Unable to connect to registration service. Please check your internet connection and try again.');
    networkError.code = 'NETWORK_ERROR';
    throw networkError;
  }
};

/**
 * Get user-friendly error message for registration errors
 * @param {Object} error - Error object with code and message
 * @returns {string} User-friendly error message
 */
export const getRegistrationErrorMessage = (error) => {
  const errorMessages = {
    INVALID_PRODUCT: 'The selected product is not valid. Please try again with a different product.',
    INVALID_SERIAL: 'The serial number provided is not valid. Please check and try again.',
    DUPLICATE_REGISTRATION: 'This product has already been registered.',
    VALIDATION_ERROR: 'Please check your registration information and try again.',
    NETWORK_ERROR: 'Unable to connect to registration service. Please check your internet connection and try again.',
    UNKNOWN_ERROR: 'An unexpected error occurred. Please try again later.'
  };

  return errorMessages[error.code] || errorMessages.UNKNOWN_ERROR;
}; 